import { CoveredUncoveredEnum } from 'plan-library-common'
import {
  IShellPlan,
  ShellArea,
  ShellGarage,
  ShellOverview,
  ShellStructure,
  ShellStructureSpecifications,
} from 'src/types/shell.interface'
import {
  IUnitPlan,
  UnitAdditionalFacilities,
  UnitAdditionalRooms,
  UnitArea,
  UnitGarage,
  UnitOverview,
  UnitPrice,
  UnitStructure,
  UnitStructureSpecifications,
} from 'src/types/unit.interface'

import {
  AdditionalOptions,
  AdditionalPlanAttributes,
  Bathrooms,
  IDraftPlan,
  IPlan,
  IPlanInfo,
  ParentInfo,
  PlanMasterData,
  Specifications,
  SupportingDocuments,
} from '../../types/plan.interface'

const initialShellOverviewData: ShellOverview = {
  division: '',
  marketingName: '',
  region: '',
  shellCategory: '',
  shellElevation: '',
  shellStatus: '',
  shellType: '',
  structureType: '',
  yearOfCreation: 0,
}

const initialShellStructureData: ShellStructure = {
  foundationType: '',
  stories: 0,
  units: 0,
}

const initialShellAreaData: ShellArea = {
  buildingDepthInch: 0,
  buildingSqFt: 0,
  buildingWidthInch: 0,
  underRoofSqFt: 0,
}

const initialShellGarageData: ShellGarage = {
  garageLoad: [],
  garageSize: 0,
  garageType: '',
  numberOfCarGarages: 0,
}

const initialShellStructureSpecifications: ShellStructureSpecifications = {
  architecturalShake: [],
  architecturalShingle: [],
  boardAndBatten: [],
  exteriorCladding: [],
  exteriorWallConstruction: [],
  interiorWallConstruction: [],
  sidingLap: [],
  sidingPanel: [],
}

const initialPrimaryShellElevationPlanWithDefaults: IShellPlan = {
  _etag: '',
  communityPlanId: '',
  createdByCorporate: false,
  garages: initialShellGarageData,
  parentInfo: {} as ParentInfo,
  planCreatedBy: '',
  planCreatedDateTime: '',
  shellArea: initialShellAreaData,
  shellOverview: initialShellOverviewData,
  shellStructure: initialShellStructureData,
  structureSpecifications: initialShellStructureSpecifications,
  submitted: false,
  supplementalDocuments: [] as SupportingDocuments[],
  versionId: 0,
}

const initialUnitOverviewData: UnitOverview = {
  changeNumber: '',
  communityId: '',
  communityName: '',
  division: '',
  engineerName: '',
  firmName: '',
  marketingName: '',
  region: '',
  series: '',
  structureType: '',
  unitCategory: '',
  unitElevation: '',
  unitOwnedByLennar: null,
  unitStatus: '',
  unitType: '',
  valueEngineered: null,
  yearOfCreation: 0,
}

const initialUnitStructureData: UnitStructure = {
  basement: null,
  basementType: '',
  bathrooms: {} as Bathrooms,
  bedrooms: 0,
  formalDiningRoom: false,
  formalLivingRoom: false,
  foundationType: '',
  livingAreas: undefined,
  numberOfDiningAreas: 0,
  stories: 0,
}

const initialUnitAreaData: UnitArea = {
  basementSqft: null,
  eighthFloorSqft: null,
  fifthFloorSqft: null,
  firstFloorSqft: null,
  fourthFloorSqft: null,
  masterBedroomLocation: 0,
  ninthFloorSqft: null,
  secondFloorSqft: null,
  seventhFloorSqft: null,
  sixthFloorSqft: null,
  sqftOfUnitConditionedSpace: '',
  sqftOfUnitUnderRoof: null,
  thirdFloorSqft: null,
  unitDepthFeet: 0,
  unitDepthInch: 0,
  unitSqft: null,
  unitWidthFeet: 0,
  unitWidthInch: 0,
}

const initialUnitPriceData: UnitPrice = {
  baseUnitPre: '',
  baseUnitPrice: '',
  effectiveDate: '',
}

const initialUnitGarageData: UnitGarage = {
  garageLoad: [],
  garageSize: 0,
  garageType: '',
  numberOfCarGarages: 0,
}

const initialUnitStructureSpecifications: UnitStructureSpecifications = {
  entry: '',
  entrySqft: null,
  exteriorWallConstruction: [],
  interiorWallConstruction: [],
}

const initialUnitAdditionalFacilities: UnitAdditionalFacilities = {
  deck: false,
  deckSize: '',
  impactWindows: false,
  indoorFireplace: false,
  interiorSprinklers: false,
  lanaiEnclosures: false,
  lanaiSqft: null,
  numberOfIndoorFireplaces: 0,
  numberOfOutdoorFireplaces: 0,
  outdoorFireplace: false,
  outdoorKitchen: false,
  patio: false,
  patioCovering: '' as CoveredUncoveredEnum,
  patioSize: '',
  stormShutters: false,
  vaultedCeiling: false,
  vaultedCeilingLivingSpaceImpacts: '',
  vaultedCeilingLocation: [],
}

const initialUnitAdditionalRooms: UnitAdditionalRooms = {
  bonusRoom: false,
  bonusRoomLivingSpaceImpacts: '',
  bonusRoomLocation: 0,
  bonusRoomSqft: null,
  familyRoom: false,
  familyRoomLivingSpaceImpacts: '',
  familyRoomLocation: 0,
  familyRoomSqft: null,
  flex: false,
  flexLivingSpaceImpacts: '',
  flexLocation: 0,
  flexSqft: null,
  gameRoom: false,
  gameRoomLivingSpaceImpacts: '',
  gameRoomLocation: 0,
  gameRoomSqft: null,
  greatRoom: false,
  greatRoomLivingSpaceImpacts: '',
  greatRoomLocation: 0,
  greatRoomSqft: null,
  loft: false,
  loftLivingSpaceImpacts: '',
  loftLocation: 0,
  loftSqft: null,
  study: false,
  studyLivingSpaceImpacts: '',
  studyLocation: 0,
  studySqft: null,
  sunRoom: false,
  sunRoomLivingSpaceImpacts: '',
  sunRoomLocation: 0,
  sunRoomSqft: null,
}

const initialPrimaryUnitElevationPlanWithDefaults: IUnitPlan = {
  _etag: '',
  communityPlanId: '',
  createdByCorporate: false,
  garages: initialUnitGarageData,
  parentInfo: {} as ParentInfo,
  planCreatedBy: '',
  planCreatedDateTime: '',
  structureSpecifications: initialUnitStructureSpecifications,
  submitted: false,
  supplementalDocuments: [] as SupportingDocuments[],
  unitAdditionalFacilities: initialUnitAdditionalFacilities,
  unitAdditionalRooms: initialUnitAdditionalRooms,
  unitArea: initialUnitAreaData,
  unitOverview: initialUnitOverviewData,
  unitPrice: initialUnitPriceData,
  unitStructure: initialUnitStructureData,
  versionId: 0,
}

const initialPlanMasterData: PlanMasterData = {
  architecturalFirm: '',
  baseHousePre: '',
  baseHousePrice: '',
  basement: '',
  basementSqft: 0,
  basementType: '',
  bathrooms: {} as Bathrooms,
  bedrooms: '',
  buildingSqft: '',
  changeNumber: '',
  CommunityName: '',
  effectiveDate: '',
  engineerName: '',
  engineerOfRecord: '',
  firmName: '',
  floorCeilingHeightList: [
    {
      label: '1st Floor Ceiling Height',
      valueInFeet: '',
      valueInInch: '',
    },
  ],
  formalLivingRoom: false,
  foundationType: '',
  homeSqft: 0,
  livingAreas: undefined,
  marketingName: '',
  masterBedroomLocation: 1,
  PlanElevation: '',
  planOwnedByLennar: '',
  planStatus: '',
  planType: '',
  profitPercentage: '',
  series: '',
  sqftOfHomeConditionedSpace: '',
  sqftOfHomeUnderRoof: '',
  stories: '',
  valueEngineered: '',
  yearOfCreation: undefined,
}

const initialAdditionalPlanAttributes: AdditionalPlanAttributes = {
  architecturalShake: [],
  architecturalShingle: [],
  boardAndBatten: [],
  depthOfThePlanInFeet: '',
  depthOfThePlanInInch: '',
  entry: '',
  entrySqft: undefined,
  exteriorCladding: [],
  exteriorWallConstruction: [],
  formalDiningRoom: false,
  garageAlignment: '',
  garageLoad: [],
  garageSize: '',
  garageType: '',
  golfCartGarage: '',
  golfCartGarageAttachedDetached: '',
  golfCartGarageSize: '',
  interiorWallConstruction: [],
  materialRoofType: [],
  numberOfCarGarages: '',
  numberOfDiningAreas: undefined,
  optionalGarage: '',
  optionalGarageNumberOfCarGarages: '',
  optionalGarageSize: '',
  optionalGarageType: '',
  roofShape: [],
  rvGarage: '',
  rvGarageAttachedDetached: '',
  rvGarageSize: '',
  sidingLap: [],
  sidingPanel: [],
  widthOfThePlanInFeet: '',
  widthOfThePlanInInch: '',
}

const initialAdditionalOptions: AdditionalOptions = {
  additionalBathrooms: false,
  bonusRoom: false,
  bonusRoomLivingSpaceImpacts: '',
  bonusRoomLocation: 0,
  bonusRoomSqft: '',
  deck: false,
  deckSize: '',
  familyRoom: false,
  familyRoomLivingSpaceImpacts: '',
  familyRoomLocation: 0,
  familyRoomSqft: '',
  flex: false,
  flexLivingSpaceImpacts: '',
  flexLocation: 0,
  flexSqft: '',
  gameRoom: false,
  gameRoomLivingSpaceImpacts: '',
  gameRoomLocation: 0,
  gameRoomSqft: '',
  greatRoom: false,
  greatRoomLivingSpaceImpacts: '',
  greatRoomLocation: 0,
  greatRoomSqft: '',
  impactWindows: false,
  indoorFireplace: false,
  interiorSprinklers: false,
  lanai: false,
  lanaiEnclosureType: '',
  lanaiSqft: '',
  loft: false,
  loftLivingSpaceImpacts: '',
  loftLocation: 0,
  loftSqft: '',
  numberOfFixtures: '',
  numberOfIndoorFireplaces: 0,
  numberOfOutdoorFireplaces: 0,
  outdoorFireplace: false,
  outdoorKitchen: false,
  patio: false,
  patioCovering: '',
  patioSize: '',
  stormShutters: false,
  study: false,
  studyLivingSpaceImpacts: '',
  studyLocation: 0,
  studySqft: '',
  sunRoom: false,
  sunRoomLivingSpaceImpacts: '',
  sunRoomLocation: 0,
  sunRoomSqft: '',
  vaultedCeiling: false,
  vaultedCeilingLivingSpaceImpacts: '',
  vaultedCeilingLocation: [],
}

const initialSpecifications: Specifications = {
  marketingMaterials: [],
  modsy: [],
  virtualTours: [],
}

const initialPrimaryElevationPlanWithDefaults: IPlan = {
  additionalOptionsData: initialAdditionalOptions,
  additionalPlanAttributes: initialAdditionalPlanAttributes,
  hidden: false,
  isCeilingHeightsRequired: false,
  planMasterData: initialPlanMasterData,
  specifications: initialSpecifications as Specifications,
  supplementalDocuments: [] as SupportingDocuments[],
}

const initialDraftPlan: IDraftPlan = {
  autoSavedPlans: {},
  bulkEditPlansInfoData: undefined,
  eTagTracker: {},
  fileVersion: {},
  floorPlanMetadata: {},
  hasError: false,
  hasFormChanges: false,
  hasPageChanged: false,
  hidden: false,
  initialBulkEditDocuments: {},
  isAutoExtractFromDocument: false,
  isAutoSaveEnabled: false,
  isBulkEdit: false,
  isCeilingHeightsRequired: false,
  isDocumentPropagationEnabled: true,
  isDocumentUploadLoading: false,
  isEditEnabledOnReviewTab: false,
  isEditPlan: false,
  isFormDirty: false,
  isLoading: false,
  isMultiFamilyShellPlan: false,
  isMultiFamilyUnitPlan: false,
  isPrimaryElevationEnabled: true,
  lennarUrls: {},
  multiElevationPlans: {},
  multiElevationShellPlans: {},
  multiElevationUnitPlans: {},
  parentInfo: {} as ParentInfo,
  planEntryFormElevationIndex: {
    id: '',
    index: 0,
    previousIndex: 0,
  },
  planEntryFormTabValue: {
    previousValue: 0,
    value: 0,
  },
  planInfoData: {} as IPlanInfo,
  primaryElevationPlan: initialPrimaryElevationPlanWithDefaults,
  primaryElevationShellPlan: initialPrimaryShellElevationPlanWithDefaults,
  primaryElevationUnitPlan: initialPrimaryUnitElevationPlanWithDefaults,
  processedValidationErrors: [],
  sourceRegionalPlan: null,
}

export {
  initialAdditionalOptions,
  initialAdditionalPlanAttributes,
  initialDraftPlan,
  initialPlanMasterData,
  initialShellAreaData,
  initialShellGarageData,
  initialShellOverviewData,
  initialShellStructureData,
  initialShellStructureSpecifications,
  initialSpecifications,
  initialUnitAdditionalFacilities,
  initialUnitAdditionalRooms,
  initialUnitAreaData,
  initialUnitGarageData,
  initialUnitOverviewData,
  initialUnitPriceData,
  initialUnitStructureData,
  initialUnitStructureSpecifications,
}
